/**
 * Admin → Reviews & Ratings.
 *
 * Three tabs in one page:
 *   1. Moderation queue + full search (filters, status changes, owner-reply
 *      visibility control)
 *   2. Global settings (per-24h cap, edit window, approval toggles, public
 *      display flags, AI translation toggle)
 *   3. Predefined review texts CRUD (rating × EN + EL)
 *
 * The moderation table is the workhorse — admin always sees the user email
 * regardless of the user's `email_visible_to_owner` choice.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  Star, Search, CheckCircle2, XCircle, Archive, Trash2, RotateCcw,
  Eye, EyeOff, Save, Plus, Loader2, MessageSquare, Mail, MailX,
  Settings, FileText, Pencil,
} from "lucide-react";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const TABS = [
  { key: "moderation", icon: Star,     labelKey: "tab_moderation",  defaultLabel: "Moderation queue" },
  { key: "settings",   icon: Settings, labelKey: "tab_settings",    defaultLabel: "Settings" },
  { key: "predefined", icon: FileText, labelKey: "tab_predefined",  defaultLabel: "Predefined texts" },
];

const STATUS_OPTIONS = ["pending", "approved", "rejected", "archived", "deleted"];
const STATUS_COLOURS = {
  pending:  "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-rose-100 text-rose-800",
  archived: "bg-slate-200 text-slate-700",
  deleted:  "bg-zinc-200 text-zinc-700 line-through",
};


export default function AdminReviews() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("moderation");

  return (
    <div data-testid="admin-reviews-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-medium text-foreground flex items-center gap-2">
          <Star className="h-6 w-6 text-primary" />
          {t("admin_reviews:title", { defaultValue: "Reviews & Ratings" })}
        </h1>
      </div>

      <div className="flex gap-2 border-b border-border">
        {TABS.map((it) => {
          const Icon = it.icon;
          const active = tab === it.key;
          return (
            <button
              key={it.key}
              onClick={() => setTab(it.key)}
              data-testid={`admin-reviews-tab-${it.key}`}
              className={`flex items-center gap-2 px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${
                active
                  ? "border-primary text-primary font-medium"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon className="h-4 w-4" />
              {t(`admin_reviews:${it.labelKey}`, { defaultValue: it.defaultLabel })}
            </button>
          );
        })}
      </div>

      {tab === "moderation" && <ModerationTab />}
      {tab === "settings" && <SettingsTab />}
      {tab === "predefined" && <PredefinedTab />}
    </div>
  );
}


/* ============== TAB 1: MODERATION ============== */

function ModerationTab() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState({
    status: "pending",
    rating: "",
    language: "",
    has_owner_reply: "",
    email_visible: "",
    q: "",
  });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const params = useMemo(() => {
    const p = { limit: 100 };
    if (filters.status) p.status = filters.status;
    if (filters.rating) p.rating = filters.rating;
    if (filters.language) p.language = filters.language;
    if (filters.has_owner_reply !== "") p.has_owner_reply = filters.has_owner_reply;
    if (filters.email_visible !== "") p.email_visible = filters.email_visible;
    if (filters.q) p.q = filters.q;
    return p;
  }, [filters]);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/reviews`, { params });
      setRows(data.reviews || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [params.status, params.rating, params.language, params.has_owner_reply, params.email_visible]);

  const updateStatus = async (review_id, status) => {
    try {
      await axios.put(`${API}/admin/reviews/${review_id}/status`, { status });
      toast.success(t("admin_reviews:status_changed", { defaultValue: "Status updated" }));
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const setReplyStatus = async (review_id, status) => {
    try {
      await axios.put(`${API}/admin/reviews/${review_id}/owner-reply-status`, { status });
      toast.success(t("admin_reviews:reply_status_changed", { defaultValue: "Reply status updated" }));
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-card rounded-xl border border-border p-4 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Select value={filters.status || "__all__"} onValueChange={(v) => setFilters({ ...filters, status: v === "__all__" ? "" : v })}>
          <SelectTrigger data-testid="admin-reviews-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">{t("admin_reviews:all_statuses", { defaultValue: "All statuses" })}</SelectItem>
            {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.rating || "__all__"} onValueChange={(v) => setFilters({ ...filters, rating: v === "__all__" ? "" : v })}>
          <SelectTrigger data-testid="admin-reviews-filter-rating"><SelectValue placeholder={t("admin_reviews:rating", { defaultValue: "Rating" })} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">{t("admin_reviews:any_rating", { defaultValue: "Any rating" })}</SelectItem>
            {[5, 4, 3, 2, 1].map((r) => <SelectItem key={r} value={String(r)}>{`${r} ★`}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.language || "__all__"} onValueChange={(v) => setFilters({ ...filters, language: v === "__all__" ? "" : v })}>
          <SelectTrigger data-testid="admin-reviews-filter-lang"><SelectValue placeholder="Language" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">{t("admin_reviews:any_lang", { defaultValue: "Any language" })}</SelectItem>
            <SelectItem value="en">English</SelectItem>
            <SelectItem value="el">Ελληνικά</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.has_owner_reply === "" ? "__any__" : String(filters.has_owner_reply)} onValueChange={(v) => setFilters({ ...filters, has_owner_reply: v === "__any__" ? "" : v === "true" })}>
          <SelectTrigger data-testid="admin-reviews-filter-reply"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__any__">{t("admin_reviews:any_reply", { defaultValue: "Any reply state" })}</SelectItem>
            <SelectItem value="true">{t("admin_reviews:has_reply", { defaultValue: "Has owner reply" })}</SelectItem>
            <SelectItem value="false">{t("admin_reviews:no_reply", { defaultValue: "No reply yet" })}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.email_visible === "" ? "__any__" : String(filters.email_visible)} onValueChange={(v) => setFilters({ ...filters, email_visible: v === "__any__" ? "" : v === "true" })}>
          <SelectTrigger data-testid="admin-reviews-filter-email"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__any__">{t("admin_reviews:any_email", { defaultValue: "Any email visibility" })}</SelectItem>
            <SelectItem value="true">{t("admin_reviews:email_shared", { defaultValue: "Email shared with owner" })}</SelectItem>
            <SelectItem value="false">{t("admin_reviews:email_hidden", { defaultValue: "Email hidden from owner" })}</SelectItem>
          </SelectContent>
        </Select>
        <form onSubmit={(e) => { e.preventDefault(); refresh(); }} className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            placeholder={t("admin_reviews:search_placeholder", { defaultValue: "Search text, name, email…" })}
            className="pl-8"
            data-testid="admin-reviews-search"
          />
        </form>
      </div>

      <p className="text-sm text-muted-foreground">
        {loading
          ? t("admin_reviews:loading", { defaultValue: "Loading…" })
          : t("admin_reviews:results_count", { defaultValue: "{{count}} reviews", count: total })}
      </p>

      <div className="space-y-3">
        {rows.map((r) => <ReviewCard key={r.review_id} review={r} onStatus={updateStatus} onReplyStatus={setReplyStatus} onSaved={(updated) => setRows(rows.map((x) => x.review_id === updated.review_id ? { ...x, ...updated } : x))} />)}
        {!loading && rows.length === 0 && (
          <div className="bg-card rounded-xl border border-border p-12 text-center">
            <p className="text-muted-foreground">{t("admin_reviews:no_results", { defaultValue: "No reviews match the current filters." })}</p>
          </div>
        )}
      </div>
    </div>
  );
}


function ReviewCard({ review, onStatus, onReplyStatus, onSaved }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [draft, setDraft] = useState({
    rating: review.rating || 5,
    review_text: review.review_text || "",
    owner_reply_text: review.owner_reply_text || "",
    owner_reply_status: review.owner_reply_status || (review.owner_reply_text ? "visible" : ""),
  });
  const [saving, setSaving] = useState(false);
  const stars = Array.from({ length: 5 }, (_, i) => i < (review.rating || 0));
  const text = (review.review_text || "").trim()
    || review.predefined_text_en || review.predefined_text_el || "—";

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        rating: Number(draft.rating),
        review_text: draft.review_text,
        owner_reply_text: draft.owner_reply_text,
      };
      // Only send owner_reply_status when there's actually a reply to scope it to.
      if (draft.owner_reply_text || review.owner_reply_text) {
        payload.owner_reply_status = draft.owner_reply_status || "visible";
      }
      const r = await axios.put(`${API}/admin/reviews/${review.review_id}`, payload);
      toast.success(t("admin_reviews:saved", { defaultValue: "Saved" }));
      onSaved?.(r.data);
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-card rounded-xl border border-border p-4 space-y-3" data-testid={`admin-review-${review.review_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {/* venue_id used to render here as a small mono-font line below
              the name. Removed May 2026 because the venue name is sufficient
              identification — admins who need the id can hover the card
              testid (data-testid carries review_id, not venue_id). */}
          <p className="font-medium text-foreground truncate">{review.venue_name || review.venue_id}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLOURS[review.status] || "bg-muted"}`} data-testid={`admin-review-status-${review.review_id}`}>
            {review.status}
          </span>
          {!editing && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-muted-foreground hover:text-foreground transition-colors"
              title={t("admin_reviews:edit", { defaultValue: "Edit" })}
              data-testid={`edit-review-${review.review_id}`}
            >
              <Pencil className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {editing ? (
        <div className="space-y-3 bg-muted/40 rounded-lg p-3 border border-border">
          {/* Star picker */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground mr-1">{t("admin_reviews:rating", { defaultValue: "Rating" })}:</span>
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setDraft({ ...draft, rating: n })}
                data-testid={`edit-star-${n}-${review.review_id}`}
              >
                <Star className={`h-5 w-5 transition-colors ${n <= draft.rating ? "fill-yellow-500 text-yellow-500" : "text-muted-foreground"}`} />
              </button>
            ))}
          </div>
          <div>
            <Label className="text-xs">{t("admin_reviews:review_text", { defaultValue: "Review text" })}</Label>
            <Textarea
              value={draft.review_text}
              onChange={(e) => setDraft({ ...draft, review_text: e.target.value })}
              rows={3}
              className="mt-1 text-sm"
              data-testid={`edit-text-${review.review_id}`}
            />
          </div>
          <div>
            <Label className="text-xs">{t("admin_reviews:owner_reply_label", { defaultValue: "Owner reply" })}</Label>
            <Textarea
              value={draft.owner_reply_text}
              onChange={(e) => setDraft({ ...draft, owner_reply_text: e.target.value })}
              rows={2}
              className="mt-1 text-sm"
              data-testid={`edit-reply-${review.review_id}`}
              placeholder={t("admin_reviews:owner_reply_placeholder", { defaultValue: "(empty to leave unchanged)" })}
            />
          </div>
          {(draft.owner_reply_text || review.owner_reply_text) && (
            <div>
              <Label className="text-xs">{t("admin_reviews:reply_visibility", { defaultValue: "Reply visibility" })}</Label>
              <Select value={draft.owner_reply_status || "visible"} onValueChange={(v) => setDraft({ ...draft, owner_reply_status: v })}>
                <SelectTrigger className="mt-1" data-testid={`edit-reply-status-${review.review_id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="visible">visible</SelectItem>
                  <SelectItem value="hidden">hidden</SelectItem>
                  <SelectItem value="archived">archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={saving} data-testid={`cancel-edit-${review.review_id}`}>
              {t("admin_reviews:cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid={`save-edit-${review.review_id}`}>
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
              {t("admin_reviews:save", { defaultValue: "Save" })}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 text-sm">
            {stars.map((on, i) => (
              <Star key={i} className={`h-4 w-4 ${on ? "fill-yellow-500 text-yellow-500" : "text-muted-foreground"}`} />
            ))}
            <span className="text-xs text-muted-foreground ml-1">[{review.language || "?"}]</span>
            {review.last_admin_edited_at && (
              <span className="text-xs text-amber-600 ml-2" title={`admin edited @ ${review.last_admin_edited_at}`}>· admin-edited</span>
            )}
          </div>

          <p className="text-sm text-foreground whitespace-pre-wrap">{text}</p>
          {review.translated_en && review.language === "el" && (
            <p className="text-xs text-muted-foreground italic">EN: {review.translated_en}</p>
          )}
          {review.translated_el && review.language === "en" && (
            <p className="text-xs text-muted-foreground italic">EL: {review.translated_el}</p>
          )}
        </>
      )}

      <div className="flex items-center justify-between text-xs text-muted-foreground border-t pt-2">
        <span>
          {review.user_name_snapshot} · <span className="font-mono">{review.user_email}</span>
          {review.email_visible_to_owner
            ? <Mail className="inline h-3 w-3 ml-1 text-emerald-600" title={t("admin_reviews:email_shared")} />
            : <MailX className="inline h-3 w-3 ml-1" title={t("admin_reviews:email_hidden")} />}
        </span>
        <span>{review.submitted_at?.slice(0, 16)?.replace("T", " ")}</span>
      </div>

      {review.owner_reply_text && (
        <div className="bg-muted/40 rounded-lg p-3 text-sm border-l-2 border-primary">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <MessageSquare className="h-3 w-3" /> {t("admin_reviews:owner_reply", { defaultValue: "Owner reply" })}
              <span className={`ml-1 px-1.5 py-0.5 text-[10px] rounded ${review.owner_reply_status === "visible" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                {review.owner_reply_status || "visible"}
              </span>
            </span>
            <div className="flex gap-1">
              <button onClick={() => onReplyStatus(review.review_id, "visible")} title="Make reply visible" data-testid={`reply-visible-${review.review_id}`}><Eye className="h-3 w-3 text-emerald-600" /></button>
              <button onClick={() => onReplyStatus(review.review_id, "hidden")} title="Hide reply" data-testid={`reply-hidden-${review.review_id}`}><EyeOff className="h-3 w-3 text-amber-600" /></button>
              <button onClick={() => onReplyStatus(review.review_id, "archived")} title="Archive reply" data-testid={`reply-archived-${review.review_id}`}><Archive className="h-3 w-3" /></button>
            </div>
          </div>
          <p className="whitespace-pre-wrap">{review.owner_reply_text}</p>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {review.status !== "approved" && (
          <Button size="sm" variant="default" onClick={() => onStatus(review.review_id, "approved")} data-testid={`approve-${review.review_id}`}>
            <CheckCircle2 className="h-4 w-4 mr-1" /> {t("admin_reviews:approve", { defaultValue: "Approve" })}
          </Button>
        )}
        {review.status !== "rejected" && (
          <Button size="sm" variant="outline" onClick={() => onStatus(review.review_id, "rejected")} data-testid={`reject-${review.review_id}`}>
            <XCircle className="h-4 w-4 mr-1" /> {t("admin_reviews:reject", { defaultValue: "Reject" })}
          </Button>
        )}
        {review.status !== "archived" && (
          <Button size="sm" variant="outline" onClick={() => onStatus(review.review_id, "archived")} data-testid={`archive-${review.review_id}`}>
            <Archive className="h-4 w-4 mr-1" /> {t("admin_reviews:archive", { defaultValue: "Archive" })}
          </Button>
        )}
        {review.status === "archived" && (
          <Button size="sm" variant="outline" onClick={() => onStatus(review.review_id, "approved")} data-testid={`restore-${review.review_id}`}>
            <RotateCcw className="h-4 w-4 mr-1" /> {t("admin_reviews:restore", { defaultValue: "Restore" })}
          </Button>
        )}
        {/* Hard delete — destroys the row entirely. We keep the soft Archive
            path above for cases where the admin wants to hide but preserve the
            row; this button is for spam/ToS violations. Confirmation is an
            in-page AlertDialog (replacing the legacy window.confirm) so the
            UI matches the Delete User pattern. */}
        <Button
          size="sm"
          variant="ghost"
          className="text-destructive"
          onClick={() => setConfirmDelete(true)}
          data-testid={`hard-delete-${review.review_id}`}
        >
          <Trash2 className="h-4 w-4 mr-1" /> {t("admin_reviews:delete", { defaultValue: "Delete" })}
        </Button>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("admin_reviews:delete_dialog_title", { defaultValue: "Delete Review" })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin_reviews:confirm_hard_delete", {
                defaultValue: "Are you sure you want to permanently delete this review? This action cannot be undone.",
              })}
              <strong className="block mt-2 font-medium">
                {review.venue_name || review.venue_id}
              </strong>
              <span className="block text-xs text-muted-foreground mt-1">
                {(review.review_text || "").slice(0, 120)}{(review.review_text || "").length > 120 ? "…" : ""}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`hard-delete-cancel-${review.review_id}`}>
              {t("admin_reviews:cancel", { defaultValue: "Cancel" })}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                try {
                  await axios.delete(`${API}/admin/reviews/${review.review_id}`);
                  toast.success(t("admin_reviews:deleted", { defaultValue: "Review deleted" }));
                  onSaved?.({ review_id: review.review_id, _removed: true });
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Failed to delete");
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid={`hard-delete-confirm-${review.review_id}`}
            >
              {t("admin_reviews:delete", { defaultValue: "Delete" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


/* ============== TAB 2: SETTINGS ============== */

function SettingsTab() {
  const { t } = useTranslation();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/review-settings`).then(({ data }) => setS(data));
  }, []);

  if (!s) return <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;

  const update = (k, v) => setS({ ...s, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/review-settings`, s);
      setS(data);
      toast.success(t("admin_reviews:settings_saved", { defaultValue: "Settings saved" }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-card rounded-xl border border-border p-6 space-y-6 max-w-3xl" data-testid="admin-reviews-settings">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <NumField label={t("admin_reviews:max_per_24h", { defaultValue: "Max reviews per user / 24h" })} value={s.max_reviews_per_user_per_24h} onChange={(v) => update("max_reviews_per_user_per_24h", v)} testId="set-max-24h" />
        <NumField label={t("admin_reviews:max_per_venue_per_month", { defaultValue: "Max reviews per venue / month" })} value={s.max_reviews_per_venue_per_month} onChange={(v) => update("max_reviews_per_venue_per_month", v)} testId="set-max-month" />
        <NumField label={t("admin_reviews:edit_window", { defaultValue: "Edit window (hours)" })} value={s.review_edit_window_hours} onChange={(v) => update("review_edit_window_hours", v)} testId="set-edit-window" />
      </div>
      <div className="space-y-3">
        <SwitchRow label={t("admin_reviews:require_approval", { defaultValue: "Reviews require admin approval" })} checked={s.reviews_require_admin_approval} onChange={(v) => update("reviews_require_admin_approval", v)} testId="set-req-approval" />
        <SwitchRow label={t("admin_reviews:owner_replies_require_approval", { defaultValue: "Owner replies require admin approval" })} checked={s.owner_replies_require_admin_approval} onChange={(v) => update("owner_replies_require_admin_approval", v)} testId="set-reply-approval" />
        <SwitchRow label={t("admin_reviews:show_rating_header", { defaultValue: "Show rating chip in venue header" })} checked={s.show_rating_in_venue_header} onChange={(v) => update("show_rating_in_venue_header", v)} testId="set-show-header" />
        <SwitchRow label={t("admin_reviews:show_rating_cards", { defaultValue: "Show rating chip in venue cards" })} checked={s.show_rating_in_venue_cards} onChange={(v) => update("show_rating_in_venue_cards", v)} testId="set-show-cards" />
        <SwitchRow label={t("admin_reviews:show_reviews_section", { defaultValue: "Show reviews section on profile" })} checked={s.show_reviews_section} onChange={(v) => update("show_reviews_section", v)} testId="set-show-section" />
        <SwitchRow label={t("admin_reviews:allow_submission", { defaultValue: "Allow new review submissions" })} checked={s.allow_review_submission} onChange={(v) => update("allow_review_submission", v)} testId="set-allow-submit" />
        <SwitchRow label={t("admin_reviews:allow_owner_replies", { defaultValue: "Allow owner replies" })} checked={s.allow_owner_replies} onChange={(v) => update("allow_owner_replies", v)} testId="set-allow-replies" />
        <SwitchRow label={t("admin_reviews:auto_translate", { defaultValue: "Auto-translate reviews EN ↔ EL via AI" })} checked={s.auto_translate_user_content} onChange={(v) => update("auto_translate_user_content", v)} testId="set-auto-translate" />
      </div>
      <Button onClick={save} disabled={saving} data-testid="set-save">
        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
        {t("admin_reviews:save_settings", { defaultValue: "Save settings" })}
      </Button>
    </div>
  );
}

function NumField({ label, value, onChange, testId }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input type="number" min={0} className="mt-1" value={value} onChange={(e) => onChange(Number(e.target.value))} data-testid={testId} />
    </div>
  );
}

function SwitchRow({ label, checked, onChange, testId }) {
  return (
    <label className="flex items-center justify-between py-2 border-b border-border last:border-0">
      <span className="text-sm text-foreground">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
    </label>
  );
}


/* ============== TAB 3: PREDEFINED TEXTS ============== */

function PredefinedTab() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState({ rating: 5, text_en: "", text_el: "", sort_order: 0, is_active: true });
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    const { data } = await axios.get(`${API}/admin/predefined-review-texts`);
    setRows(data || []);
  };
  useEffect(() => { refresh(); }, []);

  const create = async () => {
    if (!draft.text_en.trim() || !draft.text_el.trim()) {
      toast.error(t("admin_reviews:both_langs_required", { defaultValue: "Both EN and EL text are required" }));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/predefined-review-texts`, draft);
      setDraft({ rating: 5, text_en: "", text_el: "", sort_order: 0, is_active: true });
      toast.success(t("admin_reviews:predef_added", { defaultValue: "Added" }));
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const update = async (id, patch) => {
    try {
      await axios.put(`${API}/admin/predefined-review-texts/${id}`, patch);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("admin_reviews:confirm_delete_predef", { defaultValue: "Delete this predefined text?" }))) return;
    await axios.delete(`${API}/admin/predefined-review-texts/${id}`);
    refresh();
  };

  const grouped = useMemo(() => {
    const m = {};
    for (const r of rows) (m[r.rating] = m[r.rating] || []).push(r);
    return m;
  }, [rows]);

  return (
    <div className="space-y-4">
      <div className="bg-card rounded-xl border border-border p-4 grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="admin-predef-create-row">
        <Select value={String(draft.rating)} onValueChange={(v) => setDraft({ ...draft, rating: Number(v) })}>
          <SelectTrigger data-testid="admin-predef-rating"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[5, 4, 3, 2, 1].map((r) => <SelectItem key={r} value={String(r)}>{`${r} ★`}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input value={draft.text_en} onChange={(e) => setDraft({ ...draft, text_en: e.target.value })} placeholder="English text" data-testid="admin-predef-en" />
        <Input value={draft.text_el} onChange={(e) => setDraft({ ...draft, text_el: e.target.value })} placeholder="Ελληνικά κείμενο" data-testid="admin-predef-el" />
        <Input type="number" value={draft.sort_order} onChange={(e) => setDraft({ ...draft, sort_order: Number(e.target.value) })} placeholder="Sort" />
        <Button onClick={create} disabled={loading} data-testid="admin-predef-add">
          <Plus className="h-4 w-4 mr-1" /> {t("admin_reviews:add", { defaultValue: "Add" })}
        </Button>
      </div>

      {[5, 4, 3, 2, 1].map((r) => (
        <div key={r} className="bg-card rounded-xl border border-border p-4">
          <p className="text-sm font-medium mb-2 flex items-center gap-1">{Array.from({ length: r }).map((_, i) => <Star key={i} className="h-4 w-4 fill-yellow-500 text-yellow-500" />)}</p>
          <div className="space-y-2">
            {(grouped[r] || []).map((row) => (
              <div key={row.predefined_id} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center text-sm" data-testid={`admin-predef-row-${row.predefined_id}`}>
                <Input className="md:col-span-4" value={row.text_en} onChange={(e) => update(row.predefined_id, { text_en: e.target.value })} />
                <Input className="md:col-span-4" value={row.text_el} onChange={(e) => update(row.predefined_id, { text_el: e.target.value })} />
                <Input type="number" className="md:col-span-1" value={row.sort_order ?? 0} onChange={(e) => update(row.predefined_id, { sort_order: Number(e.target.value) })} />
                <div className="md:col-span-2 flex items-center gap-2">
                  <Switch checked={row.is_active} onCheckedChange={(v) => update(row.predefined_id, { is_active: v })} />
                  <span className="text-xs text-muted-foreground">{row.is_active ? "active" : "inactive"}</span>
                </div>
                <Button size="sm" variant="ghost" className="md:col-span-1 text-destructive" onClick={() => remove(row.predefined_id)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            {(grouped[r] || []).length === 0 && <p className="text-xs text-muted-foreground">—</p>}
          </div>
        </div>
      ))}
    </div>
  );
}
